import { Link } from "react-router-dom";
import { PawPrint, ShieldCheck, GraduationCap, Award, BookOpen, ArrowRight, Blocks, Sprout, Sparkles, Languages } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import WavyDivider from "@/components/WavyDivider";
import { Reveal } from "@/components/Reveal";
import { Ladybug, WateringCan, WavyUnderline, FlowerDoodle } from "@/components/Doodles";

const ASIA_IMG = "/images/asia.jpg";

const CREDENTIALS = [
    { icon: ShieldCheck, label: "OCFS license active — NYS group family daycare" },
    { icon: GraduationCap, label: "Master's degree in Health Administration" },
    { icon: Award, label: "Bachelor's degree in Sociology" },
    { icon: BookOpen, label: "OCFS-required health and safety training" },
    { icon: Languages, label: "Bilingual: English & Spanish" },
];

const STORY = [
    "Little Seeds Flourish was created from something deeply personal to me: my love for children and my belief that every child deserves a safe, nurturing environment where they feel seen, supported, encouraged, and free to grow into who they are meant to be.",
    "As a mother of two, I understand firsthand how important it is to trust the person caring for your child. Parents aren't simply looking for a place for their children to spend the day — they are choosing an environment that will become part of their child's earliest experiences, relationships, learning, and development.",
    "I wanted to create the kind of space I would want for my own children: warm, engaging, dependable, and filled with opportunities to learn, play, explore, and build confidence.",
    "My passion for helping others grow extends far beyond childcare. I hold a Master's degree in Health Administration and a Bachelor's degree in Sociology, and my professional career has centered around program leadership, education, mentorship, facilitation, and creating environments where people feel supported and empowered.",
    "I have had the privilege of mentoring and guiding others, developing educational programs, facilitating meaningful conversations, and working with individuals and communities from many different backgrounds.",
];

const STORY_AFTER_QUOTE = [
    "I chose the name intentionally. Children are like little seeds — each one unique, developing in their own way and at their own pace. With patience, encouragement, structure, meaningful experiences, and lots of love, those little seeds can develop strong roots and flourish.",
    "At Little Seeds Flourish, my goal is to provide more than childcare. I want to create a small community where children develop socially, emotionally, intellectually, creatively, and physically through play, exploration, age-appropriate learning, positive relationships, and everyday experiences.",
    "Being a mother, mentor, facilitator, and program leader has taught me that some of the most meaningful lessons happen in ordinary moments: learning how to share, finding the courage to try something new, expressing feelings, solving a problem, helping a friend, asking questions, creating something independently, or simply discovering the joy of learning.",
];

const PHILOSOPHY = [
    "At Little Seeds Flourish, we believe there is no one-size-fits-all approach to childhood. Every child enters our program with their own personality, strengths, interests, experiences, and way of learning — and we honor those differences.",
    "Our approach is child-centered, play-based, nurturing, and developmentally appropriate. Children learn best when they feel safe enough to explore, curious enough to ask questions, and supported enough to try — even when they don't get something right the first time.",
    "Learning is thoughtfully incorporated into everyday experiences. Through stories, art, music, imaginative play, movement, sensory activities, early literacy and math experiences, outdoor exploration, and meaningful conversations, children have opportunities to develop important skills while still experiencing the joy of being a child.",
    "Just as importantly, we focus on social and emotional development. We help children learn to identify and express emotions, communicate their needs, practice kindness and empathy, develop independence, navigate friendships, solve age-appropriate problems, and build confidence in themselves.",
    "We also see ourselves as partners with families. Open communication, consistency, trust, and mutual respect between caregivers and families help us better understand and support each child.",
    "At the heart of our philosophy is the idea of flourishing. We don't expect every little seed to grow in exactly the same way or at exactly the same pace. Our responsibility is to provide the care, encouragement, structure, experiences, and love that allow each child to grow into the very best version of themselves.",
];

const FOUR = [
    { icon: Blocks, word: "Play", text: "Explore the world through imagination, movement, creativity, and hands-on experiences." },
    { icon: BookOpen, word: "Learn", text: "Develop curiosity and foundational skills through meaningful, age-appropriate activities." },
    { icon: Sprout, word: "Grow", text: "Build confidence, independence, resilience, communication skills, and positive relationships." },
    { icon: Sparkles, word: "Flourish", text: "Feel valued for who they are while discovering their individual strengths and potential." },
];

export default function About() {
    usePageTitle("Meet Asia Ataide | Founder of Little Seeds Flourish");

    return (
        <div>
            <PageHero
                eyebrow="Our story"
                title={<>Meet Asia, Founder of Little Seeds Flourish</>}
                sub="Hi, I'm Asia, founder of Little Seeds Flourish."
                doodle="bee"
            />

            {/* Founder story */}
            <section className="pb-8 md:pb-14">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[0.8fr_1.2fr] gap-10 md:gap-14 items-start">
                    <Reveal>
                        <div className="relative max-w-[320px] mx-auto md:sticky md:top-28">
                            <div className="blob-frame-3 overflow-hidden border-[6px] border-white shadow-xl aspect-square">
                                <img
                                    src={ASIA_IMG}
                                    alt="Asia Ataide, founder of Little Seeds Flourish, smiling"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <WateringCan className="absolute -bottom-5 -right-4" />
                        </div>
                    </Reveal>

                    <div>
                        <Reveal>
                            <h2 className="font-display font-semibold text-2xl md:text-3xl mb-2">My story</h2>
                        </Reveal>
                        <Reveal delay={0.04}>
                            <WavyUnderline className="block w-36 h-3 mb-6" />
                        </Reveal>
                        <Reveal delay={0.08}>
                            <div className="space-y-4 text-lg text-soilmuted leading-relaxed">
                                {STORY.map((p) => (
                                    <p key={p.slice(0, 24)}>{p}</p>
                                ))}
                                <p>Through those experiences, one idea has remained constant:</p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.14}>
                            <blockquote className="my-8 bg-sage/10 border-2 border-sage/25 rounded-[2rem] p-8 md:p-10 relative" data-testid="about-belief-quote">
                                <FlowerDoodle size={40} petals="#F6C544" className="absolute -top-5 left-8" />
                                <p className="font-display font-semibold text-3xl md:text-4xl leading-snug text-sageink text-balance">
                                    "People flourish when they feel safe, valued, connected, and encouraged."
                                </p>
                                <footer className="mt-4 font-bold text-soil/70">That belief became the heart of Little Seeds Flourish.</footer>
                            </blockquote>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <div className="space-y-4 text-lg text-soilmuted leading-relaxed">
                                {STORY_AFTER_QUOTE.map((p) => (
                                    <p key={p.slice(0, 24)}>{p}</p>
                                ))}
                                <p className="font-bold text-soil">Those are the moments I want to nurture here.</p>
                                <p className="font-bold text-sageink">
                                    Little Seeds Flourish is a place to play, learn, grow — and, most importantly, flourish.
                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.26}>
                            <Link
                                to="/contact"
                                data-testid="about-schedule-tour-button"
                                className="mt-9 inline-flex items-center gap-2 bg-sun hover:bg-suncdeep text-soil font-extrabold text-lg px-8 py-4 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all border-2 border-soil/10"
                            >
                                Schedule a Tour <ArrowRight size={19} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Teaching philosophy */}
            <div className="bg-cream">
                <WavyDivider fill="#FFF3E2" />
            </div>
            <section className="py-16 md:py-24 bg-creamsoft" aria-labelledby="philosophy-heading" data-testid="about-philosophy-section">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-4">
                            <FlowerDoodle size={15} /> How we teach
                        </p>
                        <h2 id="philosophy-heading" className="font-display font-semibold text-3xl md:text-4xl leading-tight text-balance">
                            Our Teaching Philosophy
                        </h2>
                        <WavyUnderline className="block w-44 h-3 mt-3 mb-8" color="#8FB38A" />
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="space-y-4 text-lg text-soilmuted leading-relaxed">
                            {PHILOSOPHY.map((p) => (
                                <p key={p.slice(0, 24)}>{p}</p>
                            ))}
                        </div>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {FOUR.map((f, i) => (
                            <Reveal key={f.word} delay={i * 0.08} className="h-full">
                                <div
                                    className="hover-lift bg-white border-2 border-soil/10 rounded-3xl p-6 h-full text-center"
                                    data-testid={`philosophy-card-${f.word.toLowerCase()}`}
                                >
                                    <span className="inline-flex p-3 rounded-2xl bg-sun/25 mb-3 text-soil">
                                        <f.icon size={24} />
                                    </span>
                                    <h3 className="font-display font-semibold text-xl mb-2">{f.word}</h3>
                                    <p className="text-soilmuted text-sm leading-relaxed">{f.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
            <div className="bg-creamsoft">
                <WavyDivider fill="#FFF8EC" flip />
            </div>

            {/* Credentials & home */}
            <section className="py-16 md:py-24">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6 items-start">
                    <Reveal>
                        <div className="bg-creamsoft border-2 border-soil/10 rounded-[2rem] p-7 md:p-8 h-full" data-testid="about-credentials-card">
                            <h2 className="font-display font-semibold text-2xl mb-5">Credentials</h2>
                            <ul className="space-y-3.5">
                                {CREDENTIALS.map((c) => (
                                    <li key={c.label} className="flex items-center gap-3 font-bold text-soil" data-testid="about-credential-item">
                                        <span className="inline-flex p-2 rounded-xl bg-sun/25">
                                            <c.icon size={19} className="text-soil" />
                                        </span>
                                        {c.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="bg-skysoft border-2 border-sky rounded-[2rem] p-7 md:p-8 h-full flex gap-5" data-testid="about-pets-card">
                            <span className="inline-flex p-3 rounded-2xl bg-white h-fit shrink-0">
                                <PawPrint size={26} className="text-coraldeep" />
                            </span>
                            <div>
                                <h2 className="font-display font-semibold text-2xl mb-2">Our Family & Home</h2>
                                <p className="text-soilmuted leading-relaxed">
                                    Our home includes two small, friendly dogs — a Pekingese and a Shih Tzu. They are vaccinated
                                    and kept completely separate from children during program hours. We believe families should
                                    feel informed and comfortable with their child's care environment, and your child's safety and
                                    well-being always come first.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand title="Come meet the person behind the promise." />
        </div>
    );
}
